import { OverlayType } from '../constants/common'
import PaneModel from '../models/pane'
import { dateFromUnixTimestamp } from '../utils'
import Overlay from './overlay'

interface Attrs {
  period: number
  color: string
}

const datesAreOnSameDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate()

class VWAP extends Overlay<Attrs> {
  static type = OverlayType.Vwap
  static label = 'VWAP (intraday charts)'

  static getNumOfBarsBuffer() {
    return 1
  }

  constructor(values: Partial<Attrs>, model: PaneModel) {
    super(values, model)
    this.attrs.period = 5
  }

  renderContent(context: CanvasRenderingContext2D) {
    super.renderContent()
    if (!this.isRendered()) {
      return
    }

    const { leftOffset, width } = this.model.chart()
    context.translate(0.5, 0.5)
    context.beginPath()
    context.set('lineWidth', 1)
    context.set('strokeStyle', this.attrs.color)

    let cumulativePV = 0
    let cumulativeVolume = 0
    for (let i = 0; i < this.data.close.length; i++) {
      if (i > 0) {
        const prevDate = dateFromUnixTimestamp(this.data.date[i - 1])
        const date = dateFromUnixTimestamp(this.data.date[i])
        if (!datesAreOnSameDay(prevDate, date)) {
          cumulativePV = 0
          cumulativeVolume = 0
        }
      }

      const typicalPrice = (this.data.close[i] + this.data.low[i] + this.data.high[i]) / 3
      const pv = typicalPrice * this.data.volume[i]

      cumulativePV += pv
      cumulativeVolume += this.data.volume[i]

      const vwap = cumulativePV / cumulativeVolume

      const x = this.fx(i)
      if (x + leftOffset < -10) {
        continue
      }
      if (x + leftOffset > width + 10) {
        break
      }
      const y = Math.round(this.fy(vwap))
      context.lineTo(x, y)
    }
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  getModalConfig() {
    const color = {
      type: 'color',
      label: 'Color',
      name: 'color',
      value: this.attrs.color ?? this.getFreeColor(),
    }

    return {
      title: this.label,
      inputs: [color],
      inputsErrorMessages: {},
    }
  }

  getIsValid(key: string) {
    switch (key) {
      case 'color':
        // Some users have wrong colors which break the form validation
        return true
      default:
        return false
    }
  }

  isRendered() {
    return this.model.chart().quote().isIntraday
  }

  isRenderedOverlaysLabel() {
    return this.isRendered()
  }

  toString() {
    return `VWAP`
  }
}

VWAP.prototype.defaults = { color: '#9467bd' }

export default VWAP
